import config from '../config/infrastructure_outputs.json';
/**
 * Centralized API configuration and client for the Kolega Platform
 */

export interface RequestOptions extends RequestInit {
  token?: string | null;
  impersonateUserId?: string | null;
}

/**
 * Error type that preserves HTTP status and response data
 */
export class ApiError extends Error {
  status: number;
  data?: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

/**
 * Get the base API URL from environment variables or infrastructure outputs
 */
export const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_URL || config.ApiUrl || 'http://localhost:8000';
};

/**
 * Get the WebSocket base URL from the API base URL
 */
export const getWebSocketBaseUrl = (): string => {
  const apiUrl = getApiBaseUrl();
  return apiUrl.replace(/^http/, 'ws');
};

/**
 * Base API client for making authenticated and unauthenticated requests
 */
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  /**
   * Get the current base URL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Make a request to the API
   */
  async request<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { token, impersonateUserId, ...init } = options;
    const headers = new Headers(init.headers);

    // Set default headers
    if (!headers.has('Content-Type') && !(init.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    // Add auth token if provided
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Add impersonation header if provided
    if (impersonateUserId) {
      headers.set('X-Impersonate-User-ID', impersonateUserId);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...init,
      headers,
    });

    // Handle API responses
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      // detail may be a structured {error, message} object — never surface "[object Object]"
      const detail = data.detail;
      const message =
        (typeof detail === 'string' && detail) ||
        detail?.message ||
        data.message ||
        response.statusText ||
        'Request failed';
      throw new ApiError(message, response.status, data);
    }

    // Return JSON response if available, otherwise empty object
    return response.json().catch(() => ({})) as Promise<T>;
  }

  /**
   * Make a GET request
   */
  get<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * Make a POST request
   */
  post<T = any>(endpoint: string, data: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Make a PATCH request
   */
  patch<T = any>(endpoint: string, data: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Make a PUT request
   */
  put<T = any>(endpoint: string, data: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Make a DELETE request
   */
  delete<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Upload a file using FormData
   */
  async uploadFile<T = any>(endpoint: string, formData: FormData, options: RequestOptions = {}): Promise<T> {
    const { token, impersonateUserId, ...init } = options;
    const headers = new Headers(init.headers);

    // Don't set Content-Type for FormData - browser will set it with boundary
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Add impersonation header if provided
    if (impersonateUserId) {
      headers.set('X-Impersonate-User-ID', impersonateUserId);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...init,
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const message = data.detail || data.message || response.statusText || 'Upload failed';
      throw new ApiError(message, response.status, data);
    }

    return response.json().catch(() => ({})) as Promise<T>;
  }
}

// Create and export the API client instance
export const api = new ApiClient(getApiBaseUrl());

// Export convenience functions for common API patterns
export const apiEndpoints = {
  health: '/health',
  workspaces: '/workspaces',
  workspace: (id: string) => `/workspaces/${id}`,
  workspaceEnsureSandbox: (id: string) => `/workspaces/${id}/ensure-sandbox`,
  workspaceSandboxStatus: (id: string) => `/workspaces/${id}/sandbox-status`,
  workspaceSleepSandbox: (id: string) => `/workspaces/${id}/sleep-sandbox`,
  workspaceResetSandbox: (id: string) => `/workspaces/${id}/reset-sandbox`,
  workspaceRunningJobs: (id: string) => `/workspaces/${id}/running-jobs`,
  threads: (workspaceId: string) => `/workspaces/${workspaceId}/threads/`,
  thread: (workspaceId: string, threadId: string) => `/workspaces/${workspaceId}/threads/${threadId}`,
  messages: (workspaceId: string, threadId: string) => `/workspaces/${workspaceId}/threads/${threadId}/messages`,
  message: (workspaceId: string, threadId: string, messageId: string) =>
    `/workspaces/${workspaceId}/threads/${threadId}/messages/${messageId}`,
  
  // Project files endpoints (uploaded attachments)
  files: (workspaceId: string) => `/workspaces/${workspaceId}/project-files/`,
  file: (workspaceId: string, fileId: string) => `/workspaces/${workspaceId}/project-files/${fileId}`,
  fileContent: (workspaceId: string, fileId: string) => `/workspaces/${workspaceId}/project-files/${fileId}/content`,
  fileUpload: (workspaceId: string) => `/workspaces/${workspaceId}/project-files/upload`,
  fileUploadMultiple: (workspaceId: string) => `/workspaces/${workspaceId}/project-files/upload-multiple`,
  
  // Workspace files endpoints (code files)
  workspaceFiles: (workspaceId: string) => `/workspaces/${workspaceId}/files`,
  workspaceFileContent: (workspaceId: string) => `/workspaces/${workspaceId}/file-content`,
  workspaceSaveFile: (workspaceId: string) => `/workspaces/${workspaceId}/save-file`,
  workspaceDownloadRepository: (workspaceId: string) => `/workspaces/${workspaceId}/download-repository`,
  
  websocket: (workspaceId: string, agentType: string) => `/workspaces/${workspaceId}/event?agent_type=${agentType}`,
  
  // Template endpoints
  templates: '/templates',
  template: (id: string) => `/templates/${id}`,
  
  // GitHub integration
  githubStatus: '/github/status',
  githubConnect: '/github/connect',
  githubRepos: '/github/repos',
  
  // Admin endpoints
  admin: {
    impersonateCheck: '/admin/impersonate/check',
    subscriptionTiers: '/admin/subscription-tiers',
    subscriptionTier: (id: string) => `/admin/subscription-tiers/${id}`,
    subscriptionTierUsage: (id: string) => `/admin/subscription-tiers/${id}/usage`,
    subscriptionTierSetDefault: (id: string) => `/admin/subscription-tiers/${id}/set-default`,
    modelPricing: '/admin/model-pricing',
    modelPricingById: (id: string) => `/admin/model-pricing/${id}`,
    modelPricingProviders: '/admin/model-pricing/providers',
    modelPricingModelsByProvider: (provider: string) => `/admin/model-pricing/models/${provider}`,
    debugWorkspacePrompt: '/admin/debug/workspace-prompt',
  },
  
  // Billing endpoints
  billing: {
    balance: '/billing/balance',
    grants: '/billing/grants',
    grant: '/billing/grant',
    paymentMethods: '/billing/payment-methods',
    paymentConfig: '/billing/payment-config',
    purchaseCredits: '/billing/purchase-credits',
    autoTopupSettings: '/billing/auto-topup-settings',
    billingInfo: '/billing/billing-info',
    invoices: '/billing/invoices',
    subscription: '/billing/subscription',
    subscriptionTiers: '/billing/subscription/tiers',
    subscriptionUncancel: '/billing/subscription/uncancel',
    hasSubscriptionAccess: '/billing/has-subscription-access',
  },  
  // Domain endpoints
  domains: {
    search: '/domains/search',
    purchase: '/domains/purchase',
    byod: '/domains/byod',
    myDomains: '/domains/mine',
    domain: (id: string) => `/domains/${id}`,
    reconcile: (domainId: string) => `/domains/${domainId}/reconcile`,
    assign: (domainId: string) => `/domains/${domainId}/assign`,
    unassign: (domainId: string) => `/domains/${domainId}/unassign`,
    dnsRecords: (domainId: string) => `/domains/${domainId}/dns-records`,
    dnsRecord: (domainId: string, recordId: string) => `/domains/${domainId}/dns-records/${recordId}`,
    dnsChangeStatus: (domainId: string, changeId: string) => `/domains/${domainId}/dns-records/changes/${changeId}`,
  },
  // Integration endpoints
  integrations: {
    expo: {
      get: '/integrations/expo',
      update: '/integrations/expo',
      remove: '/integrations/expo',
    },
  },
  // Support request endpoints
  supportRequests: {
    create: '/support-requests',
    admin: {
      list: '/admin/support-requests',
      get: (ticketNumber: string) => `/admin/support-requests/${ticketNumber}`,
      updateStatus: (ticketNumber: string) => `/admin/support-requests/${ticketNumber}/status`,
    },
  },
  // Feature flags/endpoints
  features: {
    effective: '/features/effective',
    admin: {
      listFlags: '/admin/features/flags',
      upsertFlag: (key: string) => `/admin/features/flags/${key}`,
      deleteFlag: (key: string) => `/admin/features/flags/${key}`,
      getUserOverrides: (userId: string) => `/admin/features/users/${userId}/overrides`,
      upsertUserOverrides: (userId: string) => `/admin/features/users/${userId}/overrides`,
      deleteUserOverrides: (userId: string) => `/admin/features/users/${userId}/overrides`,
      listUserOverrides: (limit = 100, offset = 0) => `/admin/features/users/overrides?limit=${limit}&offset=${offset}`,
    },
  },
};
